import { redact } from './redaction';
import { mayTrack } from './trackingConsent';

/**
 * Sentry error tracking — initialized only when REACT_APP_SENTRY_DSN is set.
 *
 * COPPA note: this is a children's platform, so we must NOT ship student PII to
 * a third party. Therefore: `sendDefaultPii` is off, session replay is never
 * enabled (it records the screen), we never attach user identity, and
 * `beforeSend` scrubs emails / API keys / drops request bodies from every event.
 */

// S116: the pattern list moved to `src/utils/redaction.ts` so the console path
// is held to the same standard as this one. It was scrubbing Sentry events and
// nothing else, while `logger.warn`/`logger.error` printed the same values
// verbatim in production.
export async function initSentry(): Promise<void> {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return; // No DSN provisioned yet → no-op (safe default).

  // S223: loaded only when there is somewhere to send to. It was a static import, so every page
  // load on every device paid for an SDK that, with no DSN anywhere, did nothing.
  const Sentry = await import('@sentry/react');

  const environment =
    process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';
  const isProd = environment === 'production';

  Sentry.init({
    dsn,
    environment,
    release: process.env.REACT_APP_VERSION,
    sendDefaultPii: false,
    tracesSampleRate: isProd ? 0.1 : 1.0,
    /*
     * Task 92: performance TRANSACTIONS do not pass through `beforeSend`.
     *
     * `tracesSampleRate` above makes the SDK send its own performance data, and that is a
     * separate pipeline with a separate hook. Gating only `beforeSend` would have left a sampled
     * stream of page-load and navigation traces — each an HTTP request carrying an IP address —
     * going to a third party from a viewer who had refused tracking. Two mechanisms, both have
     * to be closed; CLAUDE.md's "when a feature has more than one mechanism, assume none of them
     * is wired and check each" applies in reverse.
     */
    beforeSendTransaction(event) {
      if (!mayTrack('analytics')) return null;
      return event;
    },
    beforeSend(event) {
      /*
       * LAUNCH_PLAN task 92 — CONSENT IS CHECKED HERE, not only at the call site.
       *
       * `monitoring.sendErrorToService` also checks, and that is not the same check. `Sentry.init`
       * installs global `error` and `unhandledrejection` handlers, so the SDK captures and
       * transmits on its own — every unhandled exception in the app, with no call site of ours
       * involved. Gating only our explicit `captureException` would have left the automatic half
       * sending, which is most of it.
       *
       * Returning `null` drops the event before it leaves the browser. Checked per event rather
       * than by deferring `init`, so a viewer who accepts mid-session starts being covered
       * immediately and one who WITHDRAWS stops immediately — a deferred init cannot be undone.
       */
      if (!mayTrack('analytics')) return null;

      // The request BODY, cookies and headers are dropped outright rather than
      // scrubbed: none of them has diagnostic value worth the risk.
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        delete event.request.headers;
      }
      // Never attach user PII (no email/name) for a kids' platform.
      delete event.user;

      // S136: everything else used to be scrubbed by hand, one field at a time,
      // and the list was short by every field this app actually populates:
      //
      //   - `extra` — the ONLY payload `monitoring.sendErrorToService` sends
      //     ({...additionalInfo, componentStack, url}), caller-supplied from
      //     error boundaries and catch blocks all over the app. Untouched, while
      //     the SAME email in `message` was redacted.
      //   - `breadcrumbs` — Sentry's default integrations record console calls,
      //     fetch URLs and DOM clicks. On a platform where children type into a
      //     chat box that gets logged, this is the highest-volume PII channel in
      //     the payload.
      //   - `request.url` and `request.query_string` — the body was deleted and
      //     the href kept, so an invite token or a share-link session id in a
      //     query string went out whole.
      //   - `tags` and `contexts`.
      //   - stack frame `filename` and `function`, which are a separate array
      //     from `exception.values[].value`.
      //
      // A deep walk cannot be short by a field. `redact` is cycle-safe and never
      // throws — a scrub that threw inside `beforeSend` would take the whole
      // event with it — and it also blanks sensitive KEYS (password, token,
      // apiKey), which no text pattern can recognise.
      return redact(event) as typeof event;
    },
  });

  // Expose for the existing secureErrorHandler / monitoring hooks that look for
  // `window.Sentry.captureException`.
  (window as unknown as { Sentry?: typeof import('@sentry/react') }).Sentry = Sentry;
}
